import * as React from 'react';

import {
  Badge,
  Button,
  ConfirmPopoverButton,
  DialogClosingConfirmation,
  EntityDetails,
  LocalizedDate,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ArrowLeft, Trash2 } from 'lucide-react';

import { itemDeleteAtom } from '../../../_graphql/mutationItemDelete';
import { itemUpdateAtom } from '../../../_graphql/mutationItemUpdate';
import * as state from '../../../_state/itemDetail.state';
import {
  $discardBaseline,
  $hasUnsavedChanges,
  $resetItemFormToItem,
  ItemFormState,
} from '../../../_state/itemForm.state';
import { useSettingsLocation } from '../../../_state/settingsModule.state';
import { usePopulateItemForm } from '../../../hooks/usePopulateItemForm';
import { useTranslations } from '../../../i18n/useTranslations';

import { ItemTabDetails } from './ItemTabDetails';

type ItemDetailTab = 'details';

export const ItemDetailView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();

  const [currentTab, setCurrentTab] = React.useState<ItemDetailTab>('details');
  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const [pendingTabChange, setPendingTabChange] =
    React.useState<ItemDetailTab | null>(null);

  const itemId = useAtomValue(state.$itemId);
  const {
    data: item,
    isFetching,
    refetch,
  } = useAtomValue(state.$remoteItemDetail);
  const isLoading = useAtomValue(state.$isItemLoading);
  const error = useAtomValue(state.$itemError);
  const hasUnsavedChanges = useAtomValue($hasUnsavedChanges);

  const [, resetForm] = useAtom($resetItemFormToItem);
  const [discardBaseline, setDiscardBaseline] = useAtom($discardBaseline);

  const deleteItem = useSetAtom(itemDeleteAtom({}));
  const updateItem = useSetAtom(itemUpdateAtom({}));

  const toast = useToast();
  usePopulateItemForm({ item });

  const handleBack = React.useCallback(() => {
    setLocation('/inventory');
  }, [setLocation]);

  const handleDiscard = React.useCallback(() => {
    resetForm();
    toast.success(t.inventoryView.itemDetailView.toast.discarded);
  }, [resetForm, toast, t]);

  const handleSaveUpdates = React.useCallback(
    async (data?: ItemFormState) => {
      if (!itemId || !data) return;

      if (!data.name.trim()) {
        toast.error(t.inventoryView.itemEditDialog.validation.nameRequired);
        return;
      }
      if (!data.skuNumber.trim()) {
        toast.error(t.inventoryView.itemEditDialog.validation.skuRequired);
        return;
      }
      if (!data.categoryId) {
        toast.error(t.inventoryView.itemEditDialog.validation.categoryRequired);
        return;
      }
      if (!data.itemGroupId) {
        toast.error(
          t.inventoryView.itemEditDialog.validation.itemGroupRequired,
        );
        return;
      }

      try {
        setIsUpdating(true);
        await updateItem({
          id: itemId,
          name: data.name.trim(),
          skuNumber: data.skuNumber.trim(),
          categoryId: data.categoryId,
          itemGroupId: data.itemGroupId,
          pricingUnitOverride: data.pricingUnitOverride ?? null,
          description: data.description.trim() || null,
          notes: data.notes.trim() || null,
        });

        setDiscardBaseline(data);
        toast.success(t.inventoryView.itemDetailView.toast.updated);
        refetch?.();
      } catch (err) {
        toast.error(
          t.inventoryView.itemDetailView.toast.updateError(
            err instanceof Error ? err.message : 'Unknown error',
          ),
        );
      } finally {
        setIsUpdating(false);
      }
    },
    [itemId, updateItem, setDiscardBaseline, toast, t, refetch],
  );

  const handleBeforeChangeTab = React.useCallback(
    (newTab: string) => {
      if (hasUnsavedChanges) {
        setPendingTabChange(newTab as ItemDetailTab);
        return false;
      }
      return true;
    },
    [hasUnsavedChanges],
  );

  const handleConfirmTabChange = React.useCallback(() => {
    if (pendingTabChange && discardBaseline) {
      resetForm();
      setCurrentTab(pendingTabChange);
    }
    setPendingTabChange(null);
  }, [pendingTabChange, discardBaseline, resetForm]);

  const handleCancelTabChange = React.useCallback(() => {
    setPendingTabChange(null);
  }, []);

  const handleDelete = React.useCallback(async () => {
    if (!itemId) return;

    try {
      setIsDeleting(true);
      await deleteItem({ id: itemId });
      toast.success(t.inventoryView.itemDetailView.messages.deleteSuccess);
      setLocation('/inventory');
    } catch (err) {
      toast.error(
        t.inventoryView.itemDetailView.messages.deleteError(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsDeleting(false);
    }
  }, [itemId, deleteItem, toast, setLocation, t]);

  const tools = React.useMemo(() => {
    if (!item) return null;
    return (
      <ConfirmPopoverButton
        onConfirm={handleDelete}
        message={t.inventoryView.itemDetailView.messages.deleteConfirmation}
        confirmLabel={t.inventoryView.common.confirmDelete}
        cancelLabel={t.inventoryView.common.cancel}
        buttonProps={{
          variant: 'outline',
          color: 'failure',
          icon: <Trash2 className="size-4" />,
          isDisabled: isDeleting,
        }}
      >
        {isDeleting
          ? t.inventoryView.itemDetailView.actions.deleting
          : t.inventoryView.itemDetailView.actions.delete}
      </ConfirmPopoverButton>
    );
  }, [item, handleDelete, isDeleting, t]);

  if (isLoading || isFetching) {
    return (
      <div className="@ItemDetailView flex h-full flex-col">
        <div className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 w-1/4 rounded bg-neutral/20"></div>
            <div className="h-8 w-1/3 rounded bg-neutral/20"></div>
            <div className="h-32 rounded bg-neutral/20"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="@ItemDetailView flex h-full flex-col p-6">
        <div className="text-failure">
          Error:{' '}
          {error?.message ??
            t.inventoryView.itemDetailView.messages.itemNotFound}
        </div>
        <Button variant="outline" onPress={handleBack} className="mt-4 w-fit">
          <ArrowLeft className="mr-2 size-4" />
          {t.inventoryView.itemDetailView.actions.backToItems}
        </Button>
      </div>
    );
  }

  return (
    <div className="@ItemDetailView flex h-full flex-col">
      <EntityDetails
        className="relative"
        onChangeTab={(tab) => setCurrentTab(tab as ItemDetailTab)}
        onBeforeChangeTab={handleBeforeChangeTab}
        currentTab={currentTab}
        onBackPressed={handleBack}
        primaryTitle={item.name}
        subtitle={
          <>
            {t.inventoryView.itemDetailView.fields.sku}:{' '}
            <span className="font-mono">{item.skuNumber}</span>
            <span className="ml-2 text-neutral/60">
              • {t.inventoryView.itemDetailView.fields.created}{' '}
              <LocalizedDate date={item.timestamps.createdAt} />
            </span>
          </>
        }
        badges={
          <div className="flex gap-2">
            {item.category && (
              <Badge color="neutral" size="small">
                {item.category.name}
              </Badge>
            )}
            {item.itemGroup && (
              <Badge color="primary" size="small">
                {item.itemGroup.name}
              </Badge>
            )}
          </div>
        }
        headerActions={tools}
        tabs={{
          details: {
            title: t.inventoryView.itemDetailView.tabs.details,
            content: () => (
              <ItemTabDetails
                item={item}
                onDiscard={handleDiscard}
                onSaveUpdates={handleSaveUpdates}
                isSaving={isUpdating}
              />
            ),
          },
        }}
      />
      <DialogClosingConfirmation
        isOpen={!!pendingTabChange}
        message={t.inventoryView.itemDetailView.messages.unsavedDataMessage}
        onCancel={handleCancelTabChange}
        onDiscard={handleConfirmTabChange}
      />
    </div>
  );
};
